import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { api, formatPrice } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { ChevronDown } from "lucide-react";

const OCC_TITLES = {
  bridal: { title: "Bridal", intro: "Lehengas, anarkalis and heirloom pieces for the day and the days that follow." },
  festive: { title: "Festive", intro: "Anarkalis, suits and drape sarees for pujas, sangeets and family evenings." },
  cocktail: { title: "Cocktail", intro: "Modern silhouettes for evenings that stay up late." },
  everyday: { title: "Everyday", intro: "Handcrafted cotton and mulmul for a slower, softer everyday." },
};
const CAT_TITLES = {
  anarkali: "Anarkalis", lehenga: "Lehengas", saree: "Drape Sarees",
  suit: "Suits", kaftan: "Kaftans", "co-ord": "Co-Ord Sets",
};
const CRAFT_TITLES = { dabka: "Dabka Work", zardozi: "Zardozi", mirror: "Mirror Work", thread: "Thread Work" };

export default function Collection() {
  const params = useParams();
  const location = useLocation();
  const { currency } = useCurrency();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const [filterCraft, setFilterCraft] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [priceMax, setPriceMax] = useState(200000);

  const kind = location.pathname.startsWith("/collections/") ? "occasion"
    : location.pathname.startsWith("/shop/") ? "category"
    : location.pathname.startsWith("/craft/") ? "craft"
    : "all";

  const key = params.key;

  const heading = useMemo(() => {
    if (kind === "occasion") return OCC_TITLES[key] || { title: "Collection", intro: "" };
    if (kind === "category") return { title: CAT_TITLES[key] || "Collection", intro: "" };
    if (kind === "craft") return { title: CRAFT_TITLES[key] || "Craft", intro: "" };
    return { title: "The Atelier", intro: "" };
  }, [kind, key]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const q = new URLSearchParams();
        if (kind === "occasion") q.set("occasion", key);
        if (kind === "category") q.set("category", key);
        if (kind === "craft") q.set("craft", key);
        q.set("sort", sort);
        q.set("limit", "80");
        const { data } = await api.get(`/products?${q.toString()}`);
        setProducts(data);
      } finally { setLoading(false); }
    })();
  }, [kind, key, sort]);

  const filtered = products.filter((p) => {
    if (filterCraft && !(p.craft || []).includes(filterCraft)) return false;
    if (filterCategory && p.category !== filterCategory) return false;
    if (p.price_inr > priceMax) return false;
    return true;
  });

  return (
    <main className="pt-24 md:pt-36 pb-24" data-testid="collection-page">
      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-16 mb-14">
        <p className="text-[11px] uppercase tracking-nav text-[#5C1A1B]">{kind === "craft" ? "Craft" : "Collection"}</p>
        <h1 className="font-serif text-5xl md:text-6xl mt-3 text-[#1A1A1A]">{heading.title}</h1>
        {heading.intro && <p className="mt-5 text-[15px] text-[#4A4A4A] max-w-xl leading-relaxed">{heading.intro}</p>}
      </section>

      {/* Filters & Sort */}
      <section className="px-6 md:px-12 lg:px-16 flex flex-wrap items-center gap-4 md:gap-6 border-y border-[#BCA37F]/25 py-4 mb-10">
        <div className="text-[11px] uppercase tracking-nav text-[#7A7A7A]" data-testid="count">
          {loading ? "Loading…" : `${filtered.length} pieces`}
        </div>
        <div className="ml-auto flex items-center gap-4">
          <label className="text-[11px] uppercase tracking-nav text-[#7A7A7A] flex items-center gap-2">
            Craft
            <select value={filterCraft} onChange={(e) => setFilterCraft(e.target.value)} className="bg-transparent border-b border-[#BCA37F]/40 text-[#1A1A1A] px-1 focus:outline-none" data-testid="filter-craft">
              <option value="">All</option>
              <option value="dabka">Dabka</option>
              <option value="zardozi">Zardozi</option>
              <option value="mirror">Mirror</option>
              <option value="thread">Thread</option>
            </select>
          </label>
          {kind !== "category" && (
            <label className="text-[11px] uppercase tracking-nav text-[#7A7A7A] flex items-center gap-2">
              Category
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="bg-transparent border-b border-[#BCA37F]/40 text-[#1A1A1A] px-1 focus:outline-none" data-testid="filter-category">
                <option value="">All</option>
                {Object.entries(CAT_TITLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
          )}
          <label className="text-[11px] uppercase tracking-nav text-[#7A7A7A] flex items-center gap-2">
            Sort
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-transparent border-b border-[#BCA37F]/40 text-[#1A1A1A] px-1 focus:outline-none" data-testid="sort-select">
              <option value="newest">Newest</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="name">Name</option>
            </select>
          </label>
        </div>
      </section>

      {/* Grid */}
      <section className="px-6 md:px-12 lg:px-16">
        {filtered.length === 0 && !loading ? (
          <div className="py-20 text-center">
            <p className="font-serif text-3xl">Nothing here — yet.</p>
            <p className="text-sm text-[#7A7A7A] mt-2">Try a different filter or explore the full atelier.</p>
            <Link to="/" className="mt-6 inline-block btn-editorial-ghost">Back to Home</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 md:gap-x-6 gap-y-14 md:gap-y-20">
            {filtered.map((p) => (
              <Link to={`/product/${p.slug}`} key={p.product_id} className="product-card group" data-testid={`grid-${p.slug}`}>
                <div className="relative aspect-[3/4] overflow-hidden bg-[#F3EFEA]">
                  <img src={p.images[0]} alt={p.name} className="img-primary w-full h-full object-cover" loading="lazy" />
                  {p.images[1] && <img src={p.images[1]} alt="" className="img-secondary w-full h-full object-cover" loading="lazy" />}
                </div>
                <div className="mt-4">
                  <p className="font-serif text-xl">{p.name}</p>
                  <p className="text-[11px] uppercase tracking-nav text-[#7A7A7A] mt-1">{p.category}</p>
                  <p className="text-sm mt-2">{formatPrice(p.price_inr, currency)}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
